import { ArrowLeftIcon, TrashIcon } from "@heroicons/react/outline";
import { LoadingModal } from "Component/LoadingModal";
import { Notify } from "Component/Notify";
import { fetchApi } from "Config/fetchApi";
import React, { useRef } from "react";
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { ROUTES } from "Route/ConstantRoute";
import { handleError } from "Helper";

function CreateOrganization() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const [isLoading, setIsLoading] = useState(false);
  const [namaOrganisasi, setNamaOrganisasi] = useState("");
  const [kodeOrganisasi, setKodeOrganisasi] = useState("");
  const [emailOrganisasi, setEmailOrganisasi] = useState("");
  const [nomorTeleponKantor, setNomorTeleponKantor] = useState("");
  const [dataJurisdictionLevel, setDataJurisdictionLevel] = useState([]);
  const [dataOrganization, setDataOrganization] = useState([]);
  const [dataOrganizationFiltered, setDataOrganizationFiltered] = useState([]);
  const [tingkatYuridiksi, setTingkatYuridiksi] = useState("");
  const [organisasiInduk, setOrganisasiInduk] = useState("");
  const [jumlahPersonil, setJumlahPersonil] = useState(0);
  const [tanggalBerdiri, setTanggalBerdiri] = useState("");
  const [alamatKantor, setAlamatKantor] = useState("");

  // ==================== File Upload ==================== //
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    const validExtensions = ["image/jpeg", "image/png", "image/jpg"]; // Ekstensi yang diperbolehkan
    const maxSize = 1 * 1024 * 1024; // 1MB dalam byte

    if (file) {
      // Validasi ekstensi
      if (!validExtensions.includes(file.type)) {
        setErrorMessage("Format file tidak didukung. Gunakan JPG atau PNG.");
        setBase64("");
        setPreview(null);
        return;
      }

      // Validasi ukuran file
      if (file.size > maxSize) {
        setErrorMessage("Ukuran file terlalu besar. Maksimal 1MB.");
        setBase64("");
        setPreview(null);
        return;
      }

      // Jika validasi lolos
      setErrorMessage(null);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result);
        setBase64(reader.result); // Menampilkan pratinjau gambar
      };
      reader.readAsDataURL(file);
    }
  };
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      setErrorMessage(null);
      fileInputRef.current.click(); // Trigger file input dialog
    }
  };
  // ==================== File Upload ==================== //

  // ==================== Default ==================== //
  const goBack = () => {
    navigate(ROUTES.master_data.organization.organization_home);
  };
  const handleSuccess = (message) => {
    Notify.success({
      title: "Berhasil",
      description: message,
    });
  };

  const getDataJurisdictionLevel = async () => {
    try {
      setIsLoading(true);
      const response = await fetchApi().get(`/jurisdiction-level/get-list`);
      const filterFlagData = response.data?.data.filter(
        (item) => item.flag_active_jurisdiction_level === true
      );
      setDataJurisdictionLevel(filterFlagData);
      getDataOrganisasi();
    } catch (err) {
      setDataJurisdictionLevel([]);
      setIsLoading(false);
      handleError(err);
    }
  };
  const getDataOrganisasi = async () => {
    try {
      const paramPage = `page=0`;
      const paramSize = `size=999`;
      const response = await fetchApi().get(
        `/organization/get-list?${paramPage}&${paramSize}`
      );
      setDataOrganization(response.data?.data);
      setIsLoading(false);
    } catch (err) {
      setDataOrganization([]);
      setIsLoading(false);
      handleError(err);
    }
  };
  // ==================== Default ==================== //

  const createData = async () => {
    try {
      setIsLoading(true);
      const idJurisdictionLevel = dataJurisdictionLevel.filter((item) => {
        return item?.jurisdiction_code === tingkatYuridiksi;
      });
      const reqBody = {
        organization_photo: base64,
        organization_name: namaOrganisasi,
        organization_code: kodeOrganisasi,
        organization_office_address: alamatKantor,
        organization_office_telephone: nomorTeleponKantor,
        organization_email: emailOrganisasi,
        organization_date_establishment:
          moment(tanggalBerdiri).format("YYYY-MM-DD"),
        organization_jurisdiction_level_id:
          idJurisdictionLevel.length > 0
            ? idJurisdictionLevel[0]?.id || ""
            : "",
        organization_total_personnel: parseInt(jumlahPersonil, 10),
        organization_parent_id: organisasiInduk,
      };
      const response = await fetchApi().post(`/organization/create`, reqBody);
      setIsLoading(false);
      handleSuccess(response.data?.message);
      goBack();
    } catch (err) {
      setIsLoading(false);
      handleError(err);
    }
  };

  useEffect(() => {
    var filter = [];
    if (tingkatYuridiksi === "N" || tingkatYuridiksi === "D") {
      filter = dataOrganization.filter((item) => {
        return item?.organization_jurisdiction_level_code === "N";
      });
    } else if (tingkatYuridiksi === "R") {
      filter = dataOrganization.filter((item) => {
        return item?.organization_jurisdiction_level_code === "D";
      });
    } else if (tingkatYuridiksi === "S") {
      filter = dataOrganization.filter((item) => {
        return item?.organization_jurisdiction_level_code === "R";
      });
    }
    setDataOrganizationFiltered(filter);
  }, [tingkatYuridiksi]);

  useEffect(() => {
    getDataJurisdictionLevel();
  }, []);

  return (
    <div>
      <div className="text-lg font-bold text-start gap-2 pb-5">
        <p
          className="text-2xl font-bold"
          style={{
            fontFamily: "Poppins",
          }}
        >
          Tambah Data Organisasi
        </p>
      </div>

      <div className="rounded-2xl border-solid border-gray-300 border shadow-xl bg-white p-6">
        <div className="flex justify-between items-center px-5 w-full">
          <div className="w-2/4">
            <button
              type="submit"
              onClick={goBack}
              disabled={isLoading}
              className="bg-zinc-200 h-11 flex w-auto justify-center items-center rounded-xl hover:bg-gray-100 px-3 py-1.5 text-md font-semibold leading-6 text-black focus-visible:outline shadow-md focus-visible:outline-2 focus-visible:outline-offset-2"
            >
              <ArrowLeftIcon className="h-6 w-auto pr-2" />
              Kembali
            </button>
          </div>
        </div>

        {/* Field Kolom */}
        <div className="flex justify-center items-center py-5 px-5 w-full">
          <div className="w-full justify-center items-center">
            <center>
              <label
                htmlFor="organization_name"
                className="justify-center flex block text-md font-medium mb-5 leading-6"
              >
                Logo Organisasi
                <p className="ml-1 text-red-700">*</p>
              </label>

              <div
                onClick={triggerFileInput}
                className="cursor-pointer hover:bg-gray-100 mb-2 w-1/5 aspect-square md:h-1/4 border-2 border-gray-300 border-dashed rounded-md flex flex-col items-center justify-center"
              >
                <input
                  id="file-upload"
                  type="file"
                  name="file-upload"
                  accept="image/*"
                  className="sr-only"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                />
                {base64 !== "" ? (
                  <div className="w-full justify-center flex">
                    <center className="w-3/5 items-center justify-center">
                      <img
                        src={preview}
                        alt="Preview"
                        className="w-auto h-auto"
                      />
                    </center>
                  </div>
                ) : (
                  <div>
                    <p className="text-sm text-center">
                      {"Klik untuk mengambil gambar"}
                      {"\n\nFormat gambar .PNG, .JPEG, .JPEG"}
                      {"\nMaksimal file 1MB"}
                    </p>
                  </div>
                )}
              </div>
              {errorMessage && (
                <p className="text-red-500 mt-5 text-sm text-center">
                  {errorMessage}
                </p>
              )}
              {base64 !== "" ? (
                <button
                  className="mt-5 p-3 h-auto text-white rounded-lg font-medium disabled:bg-btn-disable disabled:cursor-not-allowed bg-primary hover:bg-opacity-90 mr-auto w-full inline-flex justify-center items-center  sm:w-auto sm:text-sm sm:mt-0"
                  onClick={() => {
                    setBase64("");
                  }}
                >
                  <TrashIcon className="w-[1.5em] aspect-square mr-2" />
                  <span>Hapus Gambar</span>
                </button>
              ) : null}
            </center>
          </div>
        </div>

        {/* Field Kolom */}
        <div className="flex justify-between items-center py-5 px-5 w-full">
          <div className="mb-2 w-2/4 mr-5">
            <label
              htmlFor="organization_name"
              className="flex block text-sm font-medium leading-6"
            >
              Nama Organisasi
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <input
                id="organization_name"
                name="organization_name"
                type="text"
                disabled={isLoading}
                value={namaOrganisasi}
                placeholder="Masukkan Nama Organisasi"
                onChange={(e) => setNamaOrganisasi(e.target.value)}
                required
                className="capitalize h-11 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div className="mb-2 w-2/4">
            <label
              htmlFor="organization_code"
              className="flex block text-sm font-medium leading-6"
            >
              Kode Organisasi
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <input
                id="organization_code"
                name="organization_code"
                type="text"
                disabled={isLoading}
                placeholder="Masukkan Kode Organisasi"
                value={kodeOrganisasi}
                onChange={(e) => setKodeOrganisasi(e.target.value)}
                required
                className="capitalize h-11 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        {/* Field Kolom */}
        <div className="flex justify-between items-center py-5 px-5 w-full">
          <div className="mb-2 w-2/4 mr-5">
            <label
              htmlFor="organization_email"
              className="flex block text-sm font-medium leading-6"
            >
              Email
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <input
                id="organization_email"
                name="organization_email"
                type="email"
                disabled={isLoading}
                value={emailOrganisasi}
                placeholder="Masukkan Email"
                onChange={(e) => setEmailOrganisasi(e.target.value)}
                required
                className="h-11 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="mb-2 w-2/4">
            <label
              htmlFor="organization_office_telephone"
              className="flex block text-sm font-medium leading-6"
            >
              Nomor Telepon Kantor
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <input
                id="organization_office_telephone"
                name="organization_office_telephone"
                type="text"
                disabled={isLoading}
                value={nomorTeleponKantor}
                placeholder="Masukkan Nomor Telepon Kantor"
                onChange={(e) => setNomorTeleponKantor(e.target.value)}
                required
                className="h-11 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        {/* Field Kolom */}
        <div className="flex justify-between items-center py-5 px-5 w-full">
          <div className="mb-2 w-2/4 mr-5">
            <label
              htmlFor="organization_jurisdiction_level_id"
              className="flex block text-sm font-medium leading-6"
            >
              Tingkat Yuridiksi
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <div className="relative w-full">
                <select
                  id="organization_jurisdiction_level_id"
                  name="organization_jurisdiction_level_id"
                  required
                  disabled={isLoading}
                  value={tingkatYuridiksi}
                  onChange={(e) => {
                    setOrganisasiInduk("");
                    setTingkatYuridiksi(e.target.value);
                  }}
                  className={`${isLoading ? "bg-zinc-100" : ""} h-11 block px-4 pr-10 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 appearance-none`}
                >
                  <option value="" disabled selected hidden>
                    Pilih Tingkat Yuridiksi
                  </option>
                  {dataJurisdictionLevel.map((item) => (
                    <option value={item.jurisdiction_code}>
                      {item.jurisdiction_name}
                    </option>
                  ))}
                </select>

                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
          <div className="mb-2 w-2/4">
            <label
              htmlFor="organization_jurisdiction_level_id"
              className="flex block text-sm font-medium leading-6"
            >
              Organisasi Induk
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <div className="relative w-full">
                <select
                  id="organization_jurisdiction_level_id"
                  name="organization_jurisdiction_level_id"
                  required
                  value={organisasiInduk}
                  disabled={tingkatYuridiksi === "" || isLoading}
                  onChange={(e) => setOrganisasiInduk(e.target.value)}
                  className={`${
                    tingkatYuridiksi === "" || isLoading ? "bg-zinc-100" : ""
                  } h-11 block px-4 pr-10 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6 appearance-none`}
                >
                  <option value="" disabled selected hidden>
                    Pilih Organisasi Induk
                  </option>
                  {dataOrganizationFiltered.map((item) => (
                    <option value={item.id}>{item.organization_name}</option>
                  ))}
                </select>
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="2"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Field Kolom */}
        <div className="flex justify-between items-center py-5 px-5 w-full">
          <div className="mb-2 w-2/4 mr-5">
            <label
              htmlFor="organization_total_personnel"
              className="flex block text-sm font-medium leading-6"
            >
              Jumlah Personil
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <input
                id="organization_total_personnel"
                name="organization_total_personnel"
                type="number"
                disabled={isLoading}
                value={jumlahPersonil}
                placeholder="Masukkan Jumlah Personil"
                onChange={(e) => setJumlahPersonil(e.target.value)}
                required
                className="h-11 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          <div className="mb-2 w-2/4">
            <label
              htmlFor="organization_date_establishment"
              className="flex block text-sm font-medium leading-6"
            >
              Tanggal Organisasi Terbentuk
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <input
                id="organization_date_establishment"
                name="organization_date_establishment"
                type="date"
                placeholder="DD-MM-YYYY"
                disabled={isLoading}
                value={tanggalBerdiri}
                onChange={(e) => setTanggalBerdiri(e.target.value)}
                required
                className="h-11 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        {/* Field Kolom */}
        <div className="flex justify-between items-center py-5 px-5 w-full">
          <div className="mb-2 w-full">
            <label
              htmlFor="organization_office_address"
              className="flex block text-sm font-medium leading-6"
            >
              Alamat Kantor
              <p className="ml-1 text-red-700">*</p>
            </label>
            <div className="mt-2">
              <textarea
                id="organization_office_address"
                name="organization_office_address"
                type="text"
                disabled={isLoading}
                value={alamatKantor}
                placeholder="Masukkan Alamat Kantor"
                onChange={(e) => setAlamatKantor(e.target.value)}
                required
                className="h-28 block px-2 w-full rounded-xl border-0 py-1.5 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 placeholder:italic focus:ring-2 focus:ring-inset focus:ring-blue-500 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end mr-5">
          <button
            type="submit"
            onClick={() => {
              if (base64 === "") {
                setErrorMessage("Logo tidak boloh kosong");
              } else {
                setErrorMessage("");
                createData();
              }
            }}
            className="h-11 flex w-auto justify-center items-center rounded-xl bg-btn-login hover:bg-opacity-50 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm  focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
          >
            Simpan
          </button>
        </div>
      </div>

      <LoadingModal open={isLoading} />
    </div>
  );
}

export default CreateOrganization;
